import { Component, OnInit, AfterViewInit, ChangeDetectorRef, Renderer2, OnDestroy } from '@angular/core';
import { PlatformService } from '@app/shared/services/platform/platform.service';
import { IAppState } from '@app/store';
import * as fromDevice from '@app/store/actions/device.action';
import * as fromRouter from '@app/store/actions/router.action';
import * as HeaderActions from '@app/core/header/store/actions/header.action';
import { Store } from '@ngrx/store';
import { LocaleService } from '@app/core/i18n';
import { PageService } from '@app/core/content/services';
import { SetDeal } from '@app/+catalog/store';
import { Router, NavigationEnd, PRIMARY_OUTLET, UrlSegmentGroup, UrlSegment } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, delay, takeUntil } from 'rxjs/operators';
import { defaultMetas } from '@app/shared/static/metas';
import * as fromUser from '@app/core/user/store/actions';
import { ShippingService } from './shared/services/shipping/shipping.service';
import { SetDirectShippingEligibility } from './core/i18n/store/actions';
import { HelpersService } from '@app/shared/services/helpers/helpers.service';
import { environment } from '@env/environment';
import { chatPathMatcher } from './+chat/chat-routing.module';
import { I18nData } from './core/i18n/services';
import { DealsService } from '@vestiaire/api-sdk-angular';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { SKIP_LINKS } from '@app/core/skip-links/skip-links.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  public hasPromoBanner = false;
  public hasBanner: boolean;
  public isHomePage: boolean;
  public isLandingPage: boolean;
  public isEditoPage: boolean;
  public pageType: string;
  public webview: boolean;
  public os = {
    isIos: false,
    isAndroid: false
  };
  public showAccessibilityLink = false;
  public enableAccessibility = true;

  public skipLinksList = SKIP_LINKS;

  private assetsPath = environment.assetsPath;
  private CssVersion = environment.designSystemVersion;
  i18nvalue: string;
  skipLinkPath: string;

  constructor(
    private platformService: PlatformService,
    private localeService: LocaleService,
    private store: Store<IAppState>,
    private pageService: PageService,
    private router: Router,
    private shippingService: ShippingService,
    protected readonly helpersService: HelpersService,
    private cd: ChangeDetectorRef,
    private renderer: Renderer2,
    private i18nData: I18nData,
    private dealsService: DealsService,
    private transferState: TransferState
  ) {
    this.i18nData.load();
    // this.injectMainCSSLinnk();
    this.updateMetas();
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(
      (event: NavigationEnd) => {
        this.store.dispatch(new fromRouter.NavigationEnd(event));
        this.updateCanonical(event.url);
        this.webview = this.isWebview(event.url) || this.isChatPage(event.url);
      },
      error => console.log(error)
    );
    this.pageService.setLanguage(this.localeService.getLocale().toString());
    this.updateDeviceState();
    this.isSelectedPage();
    this.getOperatingSystem();
    this.getI18n();
    this.showAccessibilityLink =
      this.localeService.getSiteId() === '6' &&
      this.localeService.getLocale()['language'] === 'en' &&
      this.localeService.getLocale()['region'] === 'US' &&
      !this.webview &&
      environment.feature.usablenet_accessibility;
    this.setDeals();

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(
      () => {
        if (!this.endsWithAny(this.skipLinksList, this.router.url)) {
          this.skipLinkPath = `${this.router.url}`;
        }
      },
      err => {
        console.error(err);
      }
    );
  }

  ngOnInit() {
    this.helpersService.hasBanner.pipe(delay(0)).subscribe(
      (hasBanner: boolean) => {
        this.hasBanner = hasBanner;
      },
      error => {
        console.log(error);
      }
    );

    // unregister service worker
    /*if (this.platformService.isBrowser) {
      if (window.navigator && navigator.serviceWorker) {
        navigator.serviceWorker.getRegistrations().then(function(registrations) {
          for (const registration of registrations) {
            registration.unregister();
          }
        });
      }
    }*/
  }

  ngAfterViewInit() {
    if (this.platformService.isBrowser) {
      const countryCode = this.localeService.getCountry();
      this.shippingService.getDirectShippingEligibility(countryCode).subscribe({
        next: isEligible => {
          this.store.dispatch(new SetDirectShippingEligibility(isEligible));
        }
      });
      this.store.dispatch(new fromUser.ReconnectUser());
      this.cd.detectChanges();
      if (this.showAccessibilityLink) {
        this.injectAccessibilityScript();
      }
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
  }

  onActivate() {
    if (document && document.body) {
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    }
  }

  updateMetas(): void {
    this.pageService.setDescription(defaultMetas[this.localeService.getLanguage()].description);
    this.pageService.setKeywords(defaultMetas[this.localeService.getLanguage()].keywords);
    this.pageService.setTitle(defaultMetas[this.localeService.getLanguage()].title);
  }

  updateCanonical(url: string): void {
    const parsedUrl = this.router.parseUrl(url);
    parsedUrl.fragment = null;
    parsedUrl.queryParams = {};

    const path = this.router.serializeUrl(parsedUrl);
    this.pageService.setCanonicalVersion(path);
  }

  updateDeviceState() {
    this.store.dispatch({ type: fromDevice.SET_VIEWPORT_WIDTH });
    this.store.dispatch({ type: fromDevice.SET_IS_ANDROID });
    this.store.dispatch({ type: fromDevice.SET_IS_IOS });
    let resizeTimer = null;
    const thatStore = this.store;

    if (this.platformService.isBrowser) {
      window.onresize = e => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function() {
          thatStore.dispatch({ type: fromDevice.SET_VIEWPORT_WIDTH });
        }, 500);
      };
      thatStore.dispatch({ type: fromDevice.SET_IS_TOUCH });
    }
  }

  getOperatingSystem() {
    this.store.select('device').subscribe(
      res => {
        this.os.isAndroid = res.isAndroid;
        this.os.isIos = res.isIos;
      },
      error => {
        console.log(error);
      }
    );
  }

  // TODO: change this function to use pagetype dynamicaly
  isSelectedPage(): void {
    this.store
      .select('header')
      .pipe(delay(0))
      .subscribe(
        result => {
          if (result.pageParams) {
            this.isHomePage = result.pageParams.pageType === 'homepage';
            this.isLandingPage = result.pageParams.pageType === 'landing';
            this.isEditoPage = result.pageParams.pageType === 'edito';
            this.pageType = result.pageParams.pageType;
          }
        },
        () => {}
      );
  }

  setHasPromoBanner(value: boolean): void {
    this.hasPromoBanner = value;
  }

  sendAccessibilityTracking() {
    this.store.dispatch({
      type: HeaderActions.CLICK_ON_ACCESSIBILITY_TOP_LINK,
      payload: { event_name: 'usablenet_enable' }
    });
  }

  injectAccessibilityScript() {
    const accessibilityScript = this.renderer.createElement('script');
    this.renderer.setProperty(accessibilityScript, 'id', 'ngx_accessibility_script');
    this.renderer.setProperty(accessibilityScript, 'src', 'https://vestiairecollective.usablenet.com/pt/start');
    this.renderer.setProperty(accessibilityScript, 'type', 'text/javascript');
    document.getElementsByTagName('head')[0].insertBefore(accessibilityScript, null);
  }

  injectMainCSSLinnk() {
    const mainCSSLink = this.renderer.createElement('link');
    this.renderer.setProperty(mainCSSLink, 'rel', 'stylesheet');
    this.renderer.setProperty(mainCSSLink, 'type', 'text/css');
    this.renderer.setProperty(
      mainCSSLink,
      'href',
      this.assetsPath + 'design-system/' + this.CssVersion + '/main.min.css'
    );
    document.getElementsByTagName('head')[0].insertBefore(mainCSSLink, document.getElementsByName('robots')[0]);
  }

  isWebview(url: string): boolean {
    const parsedUrl = this.router.parseUrl(url);

    return parsedUrl.queryParams.webView || parsedUrl.queryParams.webview || parsedUrl.queryParams.native;
  }

  isChatPage(url: string): boolean {
    const parsedUrl = this.router.parseUrl(url);
    const group: UrlSegmentGroup = parsedUrl.root.children[PRIMARY_OUTLET];
    const urlSegments: UrlSegment[] = group && group.segments;

    return chatPathMatcher(urlSegments) && !this.helpersService.device.isMdUp();
  }

  getI18n() {
    this.store.select('i18n').subscribe(
      ({ currency, country, language }) => {
        if (currency && country && language) {
          const { isoCode: currencyCode, symbol: currencySymbol } = currency;
          const { name: countryName } = country;
          const { name: lang } = language;

          if (currencyCode && currencySymbol && countryName && lang) {
            this.i18nvalue = `${countryName} - ${lang} - ${
              currencySymbol !== currencyCode ? currencySymbol + ' - ' + currencyCode : currencySymbol
            }`;
          }
        }
      },
      () => {
        /* Silently catch exception */
      }
    );
  }

  setDeals() {
    const key = makeStateKey('deal');

    if (this.transferState.hasKey(key)) {
      const datas = this.transferState.get(key, null);
      this.store.dispatch(new SetDeal(datas.data));
    } else {
      this.dealsService
        .activeDeal()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          res => {
            this.transferState.set(key, res);
            if (res) {
              this.store.dispatch(new SetDeal(res.data));
            }
          },
          error => {
            console.error('No datas from DealsService', error);
          }
        );
    }
  }

  endsWithAny(suffixes: string[], string: string) {
    return suffixes.some(function(suffix) {
      return string.endsWith(suffix);
    });
  }
}
